/*
 * Hero reveal — letter by letter.
 *
 * Each .hero-text word is split into per-letter <span>s on the client, then the
 * letters rise + fade in with a smooth stagger across the whole headline. Runs
 * on load / every route change (the hero always sits at the top of the page).
 *
 * No clearProps: GSAP leaves the final inline state so the letters stay visible
 * instead of falling back to the .hero-text { opacity: 0 } base rule.
 */
import type { gsap as GSAP } from 'gsap'

import { gsap } from './core'

const splitIntoLetters = (word: HTMLElement): HTMLElement[] => {
    const existing = word.querySelectorAll<HTMLElement>('.hero-letter')
    if (existing.length) return Array.from(existing)

    const text = word.textContent ?? ''
    word.textContent = ''
    const letters: HTMLElement[] = []
    for (const char of text) {
        const span = document.createElement('span')
        span.className = 'hero-letter'
        // Preserve spaces so they still take width.
        span.textContent = char === ' ' ? ' ' : char
        word.appendChild(span)
        letters.push(span)
    }
    return letters
}

export const initHero = (ctx: GSAP.Context, scope: HTMLElement): void => {
    ctx.add(() => {
        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const containers = gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.hero-text-content'))

        containers.forEach((container) => {
            const words = gsap.utils.toArray<HTMLElement>(container.querySelectorAll('.hero-text'))
            if (!words.length) return

            const letters: HTMLElement[] = []
            words.forEach((word) => {
                word.style.opacity = '1' // reveal the word; letters control visibility
                letters.push(...splitIntoLetters(word))
            })
            if (!letters.length) return

            if (reduced) {
                gsap.set(letters, { opacity: 1, yPercent: 0 })
                return
            }

            gsap.fromTo(
                letters,
                { opacity: 0, yPercent: 110 },
                {
                    opacity: 1,
                    yPercent: 0,
                    duration: 0.7,
                    ease: 'power3.out',
                    stagger: 0.025,
                    delay: 0.15
                }
            )
        })
    })
}
